import { useEffect, useMemo, useRef, useState } from 'react';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconDownload, IconPlus } from '@tabler/icons-react';
import { toBnDigits } from '../../../../../utils/bnFormat';
import Hint from '../../../../../components/common/Hint';
import ImageActionOverlay from '../../components/image/ImageActionOverlay';
import { formatFileSize } from '../compress/compressImageEngine';
import { needsDecoding, useDisplayUrls } from './heicDisplay';

// Cut-outs are transparent, so a flat tile hides whatever the model erased.
// The checkerboard is the standard "this is see-through" cue.
const CHECKERBOARD_CLASS = 'bg-[length:14px_14px] bg-[linear-gradient(45deg,#ECE8E4_25%,transparent_25%),linear-gradient(-45deg,#ECE8E4_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#ECE8E4_75%),linear-gradient(-45deg,transparent_75%,#ECE8E4_75%)] bg-[position:0_0,0_7px,7px_-7px,-7px_0px] dark:bg-[linear-gradient(45deg,#3f3f46_25%,transparent_25%),linear-gradient(-45deg,#3f3f46_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#3f3f46_75%),linear-gradient(-45deg,transparent_75%,#3f3f46_75%)]';

/**
 * Grid cell that can be dragged to a new position. The whole card is the handle
 * — the pointer sensor only starts a drag after a few pixels of movement, so a
 * plain click still reaches the card and its action buttons.
 */
function SortableCell({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className={isDragging ? 'z-10 opacity-60' : undefined}
            {...attributes}
            {...listeners}
        >
            {children}
        </div>
    );
}

/** Drag context, only mounted for grids that actually reorder. */
function MaybeDnd({ enabled, ids, sensors, onDragEnd, children }) {
    if (!enabled) return children;
    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={ids} strategy={rectSortingStrategy}>
                {children}
            </SortableContext>
        </DndContext>
    );
}

function getOutputSize(result) {
    return result?.resizedSize ?? result?.compressedSize ?? result?.croppedSize ?? result?.blob?.size ?? 0;
}

function getSavedPercent(originalSize, outputSize) {
    if (!originalSize) return 0;
    return Math.max(0, Math.round(((originalSize - outputSize) / originalSize) * 100));
}

/**
 * Scrollable thumbnail grid of uploaded images (or processed results when available).
 */
export default function ImageGridPreview({
    files = [],
    results = [],
    variant = 'compress',
    onItemClick,
    activeIndex,
    savedIndexes,
    savedLabel,
    // Per-index blobs that stand in for the original — the crop tool renders
    // the region that was actually selected, so the card shows what the output
    // will be instead of the untouched picture.
    previews = {},
    // `{ id, icon, label, onClick(index), disabled? }` shown over the thumbnail.
    actions = [],
    // Trailing "add more" tile: a card-shaped drop target that also opens the
    // file picker, for tools that collect their files in the preview itself
    // instead of a separate list on the left.
    onAddMore,
    onAddDrop,
    addLabel,
    // Indexes still being worked on: those cards get a spinner and stop
    // responding, while everything already finished stays clickable. Lets a
    // batch be inspected as it lands instead of after the whole run.
    busyIndexes,
    // Indexes whose run failed. The card keeps its original picture and wears a
    // red badge, so one bad photo in a batch is visible without the page-wide
    // error swallowing the results that did work.
    failedIndexes,
    failedLabel,
    // One-click download for a finished card, as a hover button in the corner.
    // Only cards that actually have a result get one.
    onDownload,
    downloadLabel,
    // Tiles the see-through checkerboard behind finished results. Only for
    // output that really can be transparent — behind an opaque picture it just
    // reads as noise in the letterbox bars.
    checkerboard = false,
    // Drag-to-reorder, for tools where the order of the pictures is part of the
    // output (image → PDF). `ids` must be stable per item across renders;
    // without both props the grid stays a plain, non-draggable list.
    ids,
    onReorder,
    /**
     * Size the cards by how many there are: one picture fills the panel, two sit
     * side by side at one shared height, three or more make a grid of three per row.
     * The add tile turns into a slim bar under a one- or two-card layout, so it
     * never steals a big slot; in the grid it is a card that wraps onto a new line.
     */
    adaptive = false,
}) {
    const sortable = Boolean(onReorder && ids?.length);
    const sensors = useSensors(
        // A few pixels of slop, so clicking a card (or one of its action
        // buttons) is not read as the start of a drag.
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = ids.indexOf(active.id);
        const to = ids.indexOf(over.id);
        if (from < 0 || to < 0) return;
        onReorder(from, to);
    };

    const items = useMemo(() => {
        const total = Math.max(files.length, results.length);
        return Array.from({ length: total }, (_, i) => ({
            file: files[i] ?? null,
            result: results[i] ?? null,
        }));
    }, [files, results]);

    const signature = useMemo(
        () => items
            .map(({ file, result }, index) => {
                const filePart = file ? `${file.name}-${file.size}` : '';
                const resultPart = result
                    ? `${result.name}-${getOutputSize(result)}`
                    : '';
                const preview = previews[index];
                const previewPart = preview ? `${preview.size}-${preview.type}` : '';
                return `${filePart}:${resultPart}:${previewPart}`;
            })
            .join('|'),
        [items, previews],
    );

    // HEIC uploads are decoded before they get a URL — an `<img>` of a raw HEIC
    // file paints nothing outside Safari (see shared/heicDisplay).
    const urls = useDisplayUrls(
        items.map(({ file, result }, index) => result?.blob ?? previews[index] ?? file),
        signature,
    );

    // 1 or 2 when the adaptive layout gives the cards the whole panel; 0 = grid.
    const spotlight = adaptive && items.length > 0 && items.length <= 2 ? items.length : 0;

    // Spotlight cards are sized to the PICTURE, not to a fixed box: a fixed-height
    // slot letterboxed a portrait photo with grey bars on both sides and still left
    // empty panel below. Each card takes the height the panel can give and exactly
    // the width that height needs at the picture's own aspect ratio.
    const wrapRef = useRef(null);
    const [wrapSize, setWrapSize] = useState({ width: 0, height: 0 });
    const [ratios, setRatios] = useState({});
    useEffect(() => {
        const el = wrapRef.current;
        if (!spotlight || !el) return undefined;
        const measure = () => setWrapSize({ width: el.clientWidth, height: el.clientHeight });
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [spotlight]);

    if (!items.length) return null;

    const SPOT_GAP = 12;
    const SPOT_CAPTION = 52; // name + size strip under the picture
    const SPOT_ADD = onAddMore ? 56 + SPOT_GAP : 0;
    // clientWidth includes the wrapper's own px-0.5 padding and every card has a
    // 1px border; leaving that out made two half-width cards a few pixels too wide
    // for one row, so the second one wrapped underneath.
    const spotInnerW = Math.max(0, wrapSize.width - 4);
    const spotStacked = spotlight === 2 && spotInnerW < 600;
    // Aspect ratios are remembered per PICTURE, not per slot: deleting the first of
    // two cards moves the second into slot 0, and a slot-keyed ratio made it
    // briefly take the deleted picture's shape before its own reloaded.
    const ratioKey = (index) => {
        const f = items[index]?.file;
        return f ? `${f.name}-${f.size}-${f.lastModified}` : `slot-${index}`;
    };
    const spotRatio = (index) => ratios[ratioKey(index)] || 3 / 4;
    const spotBox = (index) => {
        const ratio = spotRatio(index);
        let maxH = Math.max(180, wrapSize.height - SPOT_CAPTION - SPOT_ADD - 4);
        if (spotStacked) maxH = Math.min(maxH, 420);
        // Two side by side share ONE picture height, so both cards (caption
        // included) are equally tall and nothing is left empty beneath the shorter
        // one; the widths follow each picture's shape.
        if (spotlight === 2 && !spotStacked) {
            const rowW = spotInnerW - SPOT_GAP - 4;
            const height = Math.min(maxH, rowW / (spotRatio(0) + spotRatio(1)));
            return { width: Math.max(0, height * ratio), height: Math.max(0, height) };
        }
        const width = Math.max(0, Math.min(spotInnerW - 2, maxH * ratio));
        return { width, height: width / ratio };
    };

    return (
        <div ref={wrapRef} className="h-full min-h-0 w-full overflow-auto px-0.5 py-0.5 [scrollbar-width:thin]">
            <MaybeDnd enabled={sortable} ids={ids} sensors={sensors} onDragEnd={handleDragEnd}>
            <div className={spotlight
                ? `flex justify-center gap-3 ${spotStacked ? 'flex-col items-center' : 'flex-wrap items-start'}`
                : adaptive
                    // Three to a row; the add card follows the pictures and wraps like any other.
                    ? 'grid grid-cols-2 gap-3 sm:grid-cols-3'
                    : 'grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4'
            }>
                {items.map(({ file, result }, index) => {
                    const url = urls[index];
                    const name = result?.name ?? file?.name ?? '';
                    const hasResult = Boolean(result);
                    const originalSize = result?.originalSize ?? file?.size ?? 0;
                    const outputSize = getOutputSize(result);
                    const savedPercent = hasResult ? getSavedPercent(originalSize, outputSize) : 0;
                    const originalWidth = result?.originalWidth;
                    const originalHeight = result?.originalHeight;
                    const outputWidth = result?.width;
                    const outputHeight = result?.height;

                    const hasOutputDimensions = hasResult
                        && outputWidth > 0
                        && outputHeight > 0;

                    const hasOriginalDimensions = hasResult
                        && originalWidth > 0
                        && originalHeight > 0;

                    const showSavedBadge = hasResult && (variant === 'compress' || variant === 'crop');

                    const busy = (busyIndexes?.has?.(index) || busyIndexes?.includes?.(index) || false)
                        && !(failedIndexes?.has?.(index) || failedIndexes?.includes?.(index));
                    const clickable = Boolean(onItemClick) && !busy;
                    const saved = savedIndexes?.has?.(index) || savedIndexes?.includes?.(index);
                    const failed = failedIndexes?.has?.(index) || failedIndexes?.includes?.(index) || false;
                    const selected = activeIndex === index;
                    const hasActions = actions.length > 0 && !busy;
                    // With actions the card cannot be a <button>: the action
                    // buttons live inside it, and nesting them is invalid.
                    const CardTag = clickable && !hasActions ? 'button' : 'div';

                    const card = (
                        <CardTag
                            // Keyed by the picture, not its slot, so deleting a card
                            // keeps the others mounted — no remount, no image reload
                            // flicker as the layout switches from two cards to one.
                            key={sortable ? undefined : (file ? ratioKey(index) : `${name}-${index}`)}
                            type={clickable && !hasActions ? 'button' : undefined}
                            role={clickable && hasActions ? 'button' : undefined}
                            tabIndex={clickable && hasActions ? 0 : undefined}
                            onClick={clickable ? () => onItemClick(index) : undefined}
                            onKeyDown={clickable && hasActions ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemClick(index); }
                            } : undefined}
                            style={spotlight && wrapSize.width ? { width: spotBox(index).width } : undefined}
                            className={`group flex flex-col overflow-hidden rounded-xl border bg-white text-left shadow-[0_1px_2px_rgba(28,24,21,0.04)] transition-colors dark:bg-zinc-900 ${
                                selected
                                    ? 'border-amber-500 ring-2 ring-amber-500/20 dark:border-amber-500 dark:ring-amber-500/20'
                                    : 'border-[#ECE8E4] dark:border-zinc-700'
                            } ${clickable ? 'hover:border-amber-400/60' : ''}`}
                        >
                            <div
                                style={spotlight && wrapSize.width ? { height: spotBox(index).height } : undefined}
                                className={`relative flex items-center justify-center overflow-hidden ${
                                spotlight ? '' : 'aspect-square'
                            } ${
                                // Only the finished result gets the checkerboard;
                                // the untouched source keeps the plain tile.
                                checkerboard && hasResult
                                    ? CHECKERBOARD_CLASS
                                    : 'bg-[#F7F5F3] dark:bg-zinc-800'
                            }`}>
                                {url ? (
                                    <img
                                        src={url}
                                        alt={name}
                                        // Fill the tile: `max-*-full` never upscales,
                                        // so a small photo floated in a gray margin
                                        // and a portrait one got gray bars. Cover by
                                        // default (the lightbox shows the whole
                                        // picture); crop previews and see-through
                                        // cut-outs keep every edge in view instead,
                                        // since their outline is the point.
                                        className={`h-full w-full ${
                                            // A spotlight slot is far wider than tall, so cropping to
                                            // fill it would cut the subject; show the whole picture.
                                            spotlight || variant === 'crop' || (checkerboard && hasResult)
                                                ? 'object-contain'
                                                : 'object-cover'
                                        }`}
                                        draggable={false}
                                        onLoad={spotlight ? (e) => {
                                            const el = e.currentTarget;
                                            if (!el.naturalWidth || !el.naturalHeight) return;
                                            const r = el.naturalWidth / el.naturalHeight;
                                            const key = ratioKey(index);
                                            setRatios((prev) => (Math.abs((prev[key] || 0) - r) < 0.001 ? prev : { ...prev, [key]: r }));
                                        } : undefined}
                                    />
                                ) : !hasResult && needsDecoding(file) ? (
                                    // A 12MP HEIC takes seconds to decode; say so
                                    // instead of leaving an empty tile.
                                    <span
                                        aria-hidden="true"
                                        className="h-6 w-6 animate-spin rounded-full border-2 border-amber-500 border-t-transparent"
                                    />
                                ) : null}
                                <span className="absolute left-1.5 top-1.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                    {toBnDigits(index + 1)}
                                </span>
                                {saved ? (
                                    <span className="absolute bottom-1.5 left-1.5 rounded-md bg-[#E3F6EC] px-1.5 py-0.5 text-[10px] font-semibold text-[#1E9E5E] dark:bg-emerald-950/40 dark:text-emerald-400">
                                        {savedLabel}
                                    </span>
                                ) : null}
                                {failed && failedLabel ? (
                                    <span className="absolute bottom-1.5 right-1.5 rounded-md bg-[#FDE8E8] px-1.5 py-0.5 text-[10px] font-semibold text-[#D64545] dark:bg-red-950/40 dark:text-red-400">
                                        {failedLabel}
                                    </span>
                                ) : null}
                                {showSavedBadge ? (
                                    <span className="absolute right-1.5 top-1.5 rounded-md bg-[#1E9E5E] px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                        −{toBnDigits(savedPercent)}%
                                    </span>
                                ) : null}
                                {hasResult && variant === 'resize' && hasOutputDimensions ? (
                                    <span className="absolute right-1.5 top-1.5 rounded-md bg-amber-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                        {toBnDigits(outputWidth)}×{toBnDigits(outputHeight)}
                                    </span>
                                ) : null}
                                {hasResult && variant === 'crop' && hasOutputDimensions ? (
                                    <span className="absolute right-1.5 top-1.5 rounded-md bg-amber-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                        {toBnDigits(outputWidth)}×{toBnDigits(outputHeight)}
                                    </span>
                                ) : null}

                                {onDownload && hasResult && !busy ? (
                                    <Hint label={downloadLabel}>
                                        <button
                                            type="button"
                                            aria-label={downloadLabel}
                                            onClick={(e) => { e.stopPropagation(); onDownload(index); }}
                                            className="absolute right-1.5 top-1.5 z-20 flex h-7 w-7 items-center justify-center rounded-lg bg-white/95 text-emerald-600 shadow-sm transition-all hover:bg-emerald-50 md:opacity-0 md:group-hover:opacity-100 dark:bg-zinc-800/95 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
                                        >
                                            <IconDownload size={15} stroke={1.9} />
                                        </button>
                                    </Hint>
                                ) : null}

                                {hasActions ? (
                                    <ImageActionOverlay actions={actions} index={index} />
                                ) : null}

                                {busy ? (
                                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/65 backdrop-blur-[3px] dark:bg-zinc-900/65">
                                        <span className="h-7 w-7 animate-spin rounded-full border-[3px] border-primary-100 border-t-primary dark:border-primary-900 dark:border-t-primary" />
                                    </div>
                                ) : null}
                            </div>
                            <div className="min-w-0 px-2 py-1.5">
                                <Hint label={name}>
                                    <div className="truncate text-[11.5px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                        {name}
                                    </div>
                                </Hint>
                                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10.5px]">
                                    {hasResult && variant === 'resize' && hasOriginalDimensions && hasOutputDimensions ? (
                                        <>
                                            <span className="text-[#A8A099] line-through dark:text-zinc-500">
                                                {toBnDigits(originalWidth)}×{toBnDigits(originalHeight)}
                                            </span>
                                            <span className="font-semibold text-[#1E9E5E]">
                                                {toBnDigits(outputWidth)}×{toBnDigits(outputHeight)}
                                            </span>
                                            <span className="text-[#A8A099] dark:text-zinc-500">
                                                ({formatFileSize(originalSize)} → {formatFileSize(outputSize)})
                                            </span>
                                        </>
                                    ) : hasResult && variant === 'crop' && hasOutputDimensions ? (
                                        <>
                                            <span className="font-semibold text-[#1E9E5E]">
                                                {toBnDigits(outputWidth)}×{toBnDigits(outputHeight)}
                                            </span>
                                            <span className="text-[#A8A099] dark:text-zinc-500">
                                                ({formatFileSize(originalSize)} → {formatFileSize(outputSize)})
                                            </span>
                                        </>
                                    ) : hasResult ? (
                                        // Same before → after line every tool uses:
                                        // the original struck through, the output in
                                        // green.
                                        <>
                                            <span className="text-[#A8A099] line-through dark:text-zinc-500">
                                                {formatFileSize(originalSize)}
                                            </span>
                                            <span className="font-semibold text-[#1E9E5E]">
                                                {formatFileSize(outputSize)}
                                            </span>
                                        </>
                                    ) : (
                                        <span className="text-[#A8A099] dark:text-zinc-500">
                                            {formatFileSize(originalSize)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </CardTag>
                    );

                    if (!sortable) return card;
                    return (
                        <SortableCell key={ids[index]} id={ids[index]}>
                            {card}
                        </SortableCell>
                    );
                })}

                {onAddMore ? (
                    <button
                        type="button"
                        onClick={onAddMore}
                        onDragOver={onAddDrop ? (e) => e.preventDefault() : undefined}
                        onDrop={onAddDrop}
                        // h-full, not aspect-square: the tile has to match the
                        // height of a card, which is a square thumbnail plus its
                        // caption strip.
                        className={`flex items-center justify-center rounded-xl border-2 border-dashed border-[#E0DCD7] bg-[#FBFAF9] text-[#A8A099] transition-colors hover:border-amber-400 hover:bg-amber-50/40 hover:text-amber-600 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-500 dark:hover:border-amber-500 dark:hover:bg-amber-900/10 ${
                            spotlight
                                ? 'h-14 w-full basis-full flex-row gap-2'
                                // In the three-per-row grid it is a card like the others: it takes
                                // the next free cell, so it lands on a new line once a row is full.
                                : `h-full flex-col gap-1.5 ${adaptive ? 'min-h-[200px]' : 'min-h-[140px]'}`
                        }`}
                    >
                        <IconPlus size={22} stroke={2} />
                        {addLabel ? <span className="px-2 text-center text-[11px] font-semibold leading-tight">{addLabel}</span> : null}
                    </button>
                ) : null}
            </div>
            </MaybeDnd>
        </div>
    );
}
